import React, { useEffect, useMemo, useRef } from 'react';
import { addDays, isSameDay, eachDayOfInterval } from 'date-fns';
import { useField, useFormikContext } from 'formik';
import { differenceWith, min } from 'lodash-es';
import { FormSection } from '../../../../../common/components/ui/Form/FormSection/FormSection';
import { FormDatePicker } from '../../../../../common/components/ui/Form/FormDatePicker/FormDatePicker';
import {
  Title,
  TitleVariant
} from '../../../../../common/components/ui/Title/Title';
import {
  Button,
  ButtonBaseColor,
  ButtonSize,
  ButtonVariant
} from '../../../../../common/components/ui/Button';
import { TextFieldPreview } from '../../../../../common/components/ui/TextFieldPreview/TextFieldPreview';
import {
  useDayOffs,
  useVacationDaysAmount
} from '../../../../../common/hooks/useDayOffs';
import { getDateUtc } from '../../../../../utils/date/date';
import {
  PositionType,
  VacationScheduleItemType,
  VacationApplicationType
} from '../../../../../graphql/types';
import {
  VacationSchedulesFormValuesProps,
  VacationsUtils
} from '../../../../../store/vacations/VacationsUtils';
import { vacationMaxStartDate, vacationMinStartDate } from '../../../constants';
import s from './VacationPeriod.module.scss';

export interface VacationPeriodProps
  extends Pick<VacationScheduleItemType, 'start' | 'end'> {
  index: number;
  position?: PositionType;
  blockName: string;
  isNewPeriod: boolean;
  isRemovable: boolean;
  vacationApps?: VacationApplicationType[];
  onRemovePeriod: (index: number) => void;
}

export const VacationPeriod = ({
  index,
  position,
  start,
  end,
  blockName,
  isNewPeriod,
  isRemovable,
  vacationApps,
  onRemovePeriod
}: VacationPeriodProps) => {
  const {
    values,
    setFieldValue
  } = useFormikContext<VacationSchedulesFormValuesProps>();

  const selectedDates = values.selectedDates.slice();

  const fieldName = `${blockName}.periods[${index}]`;
  const [{ value: daysSelected }] = useField(`${fieldName}.days`);
  const [{ value: daysAvailable }] = useField('unassigned_days');

  const selectedDaysAmount = useVacationDaysAmount({
    startDate: start,
    endDate: end
  });

  const startDate = useMemo(() => {
    return start ? new Date(getDateUtc(start)) : undefined;
  }, [start]);
  const startDateRef = useRef(startDate);

  const holidayIntervalEndDate = useMemo(() => {
    return startDate ? addDays(startDate, daysAvailable || 0) : undefined;
  }, [startDate, daysAvailable]);

  const { daysOff } = useDayOffs({
    startDate: startDate ?? undefined,
    endDate: holidayIntervalEndDate
  });

  const positionEndDate = position?.c_end_date
    ? new Date(getDateUtc(position.c_end_date))
    : undefined;

  const maxStartDate = min([positionEndDate, vacationMaxStartDate]);

  const minEndDate = useMemo(() => {
    return startDate || vacationMinStartDate;
  }, [startDate]);

  // даты заявлений на отпуск для исключения их из календаря доступных дат
  let bookedDays: Date[] = [];
  if (vacationApps && vacationApps.length) {
    const mapped = vacationApps.map((application) => {
      return Object.assign({}, application, {
        end: new Date(getDateUtc(application.end)),
        start: new Date(getDateUtc(application.start))
      });
    });

    bookedDays = mapped
      .map((application) => {
        const result = eachDayOfInterval({
          start: application.start,
          end: application.end
        });

        return result;
      })
      .flat();
  }

  if (bookedDays.length) {
    selectedDates.push(...bookedDays);
  }

  const maxEndDate = useMemo(() => {
    const availEndDate = startDate
      ? addDays(
          startDate,
          (daysAvailable || 0) + (daysSelected || 0) + daysOff.length - 1
        )
      : undefined;

    const availableDates = [availEndDate, maxStartDate, positionEndDate].filter(Boolean) as Date[];

    if (vacationApps && vacationApps.length) {
      const mapped = vacationApps.map((application) => {
        return Object.assign({}, application, {
          end: new Date(getDateUtc(application.end)),
          start: new Date(getDateUtc(application.start))
        });
      });

      const maxEndDates: Date[] = [];
      // выбираем все заявления на отпуск, где дата конца больше выбранной стартовой даты
      mapped.forEach((application) => {
        if (start && application.end > new Date(start)) {
          maxEndDates.push(application.end);
        }
      });

      // среди них выбираем ближайшую к нашей стартовой дате
      if (maxEndDates.length > 0) {
        const minDate = new Date(
          Math.min(...maxEndDates.map((date) => date.getTime()))
        );
        availableDates.push(minDate);
      }
    }

    return min(availableDates);
  }, [
    daysAvailable,
    daysSelected,
    daysOff,
    startDate,
    maxStartDate,
    start,
    vacationApps,
    positionEndDate
  ]);

  /**
   * Блокируем все выбранные ранее даты, кроме дат текущего периода,
   * чтобы даты текущего периода не блокировались, и можно было их менять
   */
  const excludedDates = useMemo(() => {
    if (!selectedDates.length) return [];
    if (!start || !end) return selectedDates;
    const currInterval = VacationsUtils.getSelectedDates([{ start, end }]);
    return differenceWith(selectedDates, currInterval, isSameDay);
  }, [selectedDates, start, end]);

  useEffect(() => {
    if (startDateRef.current !== startDate) {
      startDateRef.current = startDate;
      setFieldValue(`${fieldName}.end`, undefined);
    }
  }, [fieldName, startDate, setFieldValue]);

  useEffect(() => {
    setFieldValue(`${fieldName}.days`, selectedDaysAmount || 0);
  }, [fieldName, selectedDaysAmount, setFieldValue]);

  return (
    <div className={s.VacationPeriod}>
      <div className={s.VacationPeriod__title}>
        <Title
          className={s.VacationPeriod__titleText}
          variant={TitleVariant.h3}
        >
          {isNewPeriod ? 'Новый период' : 'Определите новые даты отпуска'}
        </Title>

        {isRemovable && (
          <Button
            variant={ButtonVariant.secondary}
            baseColor={ButtonBaseColor.red}
            size={ButtonSize.small}
            onClick={() => onRemovePeriod(index)}
          >
            Удалить
          </Button>
        )}
      </div>

      <FormSection>
        <FormDatePicker
          name={`${fieldName}.start`}
          label={'Дата начала отпуска *'}
          placeholder={'Выберите дату'}
          minDate={vacationMinStartDate}
          maxDate={maxStartDate}
          excludeDates={excludedDates}
          // disabled={!daysAvailable}
        />
      </FormSection>

      <FormSection>
        <FormDatePicker
          name={`${fieldName}.end`}
          label={'Дата окончания отпуска *'}
          placeholder={'Выберите дату'}
          minDate={minEndDate}
          maxDate={maxEndDate}
          excludeDates={excludedDates}
          // disabled={!daysAvailable}
        />
      </FormSection>

      <FormSection>
        <TextFieldPreview
          title={'Количество календарных дней отпуска:'}
          simpleView
        >
          {selectedDaysAmount || 0}
        </TextFieldPreview>
      </FormSection>
    </div>
  );
};
